import jsPDF from "jspdf";
import { format } from "date-fns";
import type { Order } from "@/hooks/useOrders";

export const generateOrderPDF = async (order: Order) => {
  const doc = new jsPDF();
  
  // Helper function to check if we need a new page
  const checkPageBreak = (currentY: number, requiredSpace: number) => {
    const pageHeight = doc.internal.pageSize.height;
    
    if (currentY + requiredSpace > pageHeight - 20) {
      doc.addPage();
      return 20; // Start from top of new page
    }
    return currentY;
  };
  
  // Set up the document
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text("Brutt: Custom Handcrafted Art & Personalized Gifts", 20, 30);
  
  // Add a line separator
  doc.setLineWidth(0.5);
  doc.line(20, 35, 190, 35);
  
  let currentY = 50;
  
  // Order ID
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Order ID:", 20, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(order.id, 60, currentY);
  currentY += 10;
  
  // Order Date
  doc.setFont("helvetica", "bold");
  doc.text("Order Date:", 20, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(order.order_date), 60, currentY);
  currentY += 20;
  
  // Product Information Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Product Information", 20, currentY);
  currentY += 15;
  
  // Product Name
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Product Name:", 20, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(order.product_name, 60, currentY);
  currentY += 10;
  
  // Product ID
  doc.setFont("helvetica", "bold");
  doc.text("Product ID:", 20, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(order.product_id, 60, currentY);
  currentY += 10;
  
  // Selected Size
  doc.setFont("helvetica", "bold");
  doc.text("Selected Variant:", 20, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(order.selected_size_name || "N/A", 60, currentY);
  currentY += 20;
  
  // Artist/Seller Information
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Seller Information", 20, currentY);
  currentY += 15;
  
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Artist ID:", 20, currentY);
  doc.setFont("helvetica", "normal");
  doc.text(order.product_artistid || "N/A", 60, currentY);
  currentY += 20;
  
  // Product Image Section
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Product Image", 20, currentY);
  currentY += 15;
  
  // Product Image
  if (order.product_main_image) {
    try {
      // Check if we need a new page for the image
      currentY = checkPageBreak(currentY, 60);
      
      // Add product image to PDF
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = () => {
          try {
            // Add image to PDF (40x40 pixels)
            doc.addImage(img, 'JPEG', 60, currentY, 40, 40);
            
            // Add clickable link annotation over the image
            doc.link(60, currentY, 40, 40, { url: order.product_main_image });
            
            // Add text below the image indicating it's clickable
            doc.setFontSize(10);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(0, 0, 255);
            doc.text("(Click image to view full size)", 60, currentY + 45);
            doc.setTextColor(0, 0, 0);
            
            currentY += 60; // Space for image and text
            resolve(true);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => {
          // If image fails to load, add text instead
          doc.setFont("helvetica", "normal");
          doc.text("Image unavailable", 60, currentY);
          currentY += 10;
          resolve(true);
        };
        img.src = order.product_main_image;
      });
    } catch (error) {
      // Fallback if image loading fails
      doc.setFont("helvetica", "normal");
      doc.text("Image unavailable", 60, currentY);
      currentY += 10;
    }
  } else {
    doc.setFont("helvetica", "normal");
    doc.text("No image available", 60, currentY);
    currentY += 10;
  }
  
  currentY += 10; // Add some space after product image
  
  // Custom Information Section
  if (order.custom_text || order.special_remarks || order.custom_photo_url) {
    // Check if we need a new page for custom information
    currentY = checkPageBreak(currentY, 50);
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Custom Information", 20, currentY);
    currentY += 15;
    
    // Custom Text
    if (order.custom_text) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Custom Text:", 20, currentY);
      doc.setFont("helvetica", "normal");
      
      // Split long text into multiple lines if needed
      const customTextLines = doc.splitTextToSize(order.custom_text, 150);
      
      // Check if we need a new page for the text
      currentY = checkPageBreak(currentY, customTextLines.length * 5 + 10);
      
      doc.text(customTextLines, 60, currentY);
      currentY += customTextLines.length * 5 + 10;
    }
    
    // Special Remarks
    if (order.special_remarks) {
      // Check if we need a new page for special remarks
      currentY = checkPageBreak(currentY, 30);
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Special Remarks:", 20, currentY);
      doc.setFont("helvetica", "normal");
      
      // Split long text into multiple lines if needed
      const remarksLines = doc.splitTextToSize(order.special_remarks, 150);
      
      // Check if we need a new page for the remarks
      currentY = checkPageBreak(currentY, remarksLines.length * 5 + 10);
      
      doc.text(remarksLines, 60, currentY);
      currentY += remarksLines.length * 5 + 10;
    }
    
    // Custom Photo
    if (order.custom_photo_url) {
      // Check if we need a new page for the custom photo
      currentY = checkPageBreak(currentY, 60);
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Custom Photo:", 20, currentY);
      currentY += 5; // Space before image
      
      try {
        // Add custom photo to PDF
        const customImg = new Image();
        customImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          customImg.onload = () => {
            try {
              // Add image to PDF (40x40 pixels)
              doc.addImage(customImg, 'JPEG', 60, currentY, 40, 40);
              
              // Add clickable link annotation over the image
              doc.link(60, currentY, 40, 40, { url: order.custom_photo_url });
              
              // Add text below the image indicating it's clickable
              doc.setFontSize(10);
              doc.setFont("helvetica", "italic");
              doc.setTextColor(0, 0, 255);
              doc.text("(Click image to view full size)", 60, currentY + 45);
              doc.setTextColor(0, 0, 0);
              
              currentY += 60; // Space for image and text
              resolve(true);
            } catch (error) {
              reject(error);
            }
          };
          customImg.onerror = () => {
            // If image fails to load, add text instead
            doc.setFont("helvetica", "normal");
            doc.text("Custom photo unavailable", 60, currentY);
            currentY += 10;
            resolve(true);
          };
          customImg.src = order.custom_photo_url;
        });
      } catch (error) {
        // Fallback if image loading fails
        doc.setFont("helvetica", "normal");
        doc.text("Custom photo unavailable", 60, currentY);
        currentY += 10;
      }
    }
  }
  
  // Save the PDF
  doc.save(`order_details_${order.id}.pdf`);
};

// Helper function to format dates
const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch {
    return "Invalid date";
  }
};
